import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type RefObject } from 'react';
import { beginHold, endHold } from './hold';

export function scrubValue(value: number, dx: number, step: number, fine: boolean, coarse: boolean, min = -Infinity, max = Infinity, decimals = 1) {
  const change = dx / 2 * step * (fine ? 0.1 : coarse ? 10 : 1);
  return Math.max(min, Math.min(max, Number((value + change).toFixed(decimals))));
}

/** Capture keeps a drag alive beyond the field; Escape restores its starting value. */
export function useNumberScrub(options: { value: number; step: number; min?: number; max?: number; decimals: number; disabled?: boolean; onChange: (value: number) => void }, input: RefObject<HTMLInputElement | null>) {
  const latest = useRef(options);
  latest.current = options;
  const drag = useRef<{ startX: number; x: number; from: number; value: number; active: boolean; target: HTMLElement; pointer: number } | null>(null);
  const [scrubbing, setScrubbing] = useState(false);
  const stop = (cancel = false, focus = false) => {
    const current = drag.current;
    if (!current) return;
    drag.current = null;
    if (cancel && current.active) latest.current.onChange(current.from);
    if (current.active) endHold();
    if (current.target.hasPointerCapture(current.pointer)) current.target.releasePointerCapture(current.pointer);
    setScrubbing(false);
    if (focus && !current.active) { input.current?.focus({ preventScroll: true }); input.current?.select(); }
  };
  const stopRef = useRef(stop);
  stopRef.current = stop;
  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && drag.current) { event.preventDefault(); event.stopPropagation(); stopRef.current(true); }
    };
    const blur = () => stopRef.current();
    window.addEventListener('keydown', key, true);
    window.addEventListener('blur', blur);
    return () => { window.removeEventListener('keydown', key, true); window.removeEventListener('blur', blur); if (drag.current?.active) endHold(); drag.current = null; };
  }, []);
  return {
    scrubbing,
    handlers: {
      onPointerDown: (event: ReactPointerEvent<HTMLElement>) => {
        if (event.button !== 0 || latest.current.disabled || event.currentTarget.matches(':disabled') || event.currentTarget.closest('fieldset:disabled')) return;
        // Once editing, retain normal caret placement and text selection.
        if (event.currentTarget === input.current && document.activeElement === input.current) return;
        event.preventDefault();
        event.stopPropagation();
        event.currentTarget.setPointerCapture(event.pointerId);
        drag.current = { startX: event.clientX, x: event.clientX, from: latest.current.value, value: latest.current.value, active: false, target: event.currentTarget, pointer: event.pointerId };
      },
      onPointerMove: (event: ReactPointerEvent<HTMLElement>) => {
        const current = drag.current;
        if (!current) return;
        if (!current.active) {
          if (Math.abs(event.clientX - current.startX) < 3) return;
          current.active = true;
          beginHold();
          setScrubbing(true);
        }
        const opt = latest.current;
        // Accumulate substeps so fractional pointer movement is not rounded away.
        const next = scrubValue(current.value, event.clientX - current.x, opt.step, event.altKey, event.shiftKey, opt.min, opt.max, 6);
        current.x = event.clientX;
        current.value = next;
        opt.onChange(Number(next.toFixed(opt.decimals)));
      },
      onPointerUp: () => stop(false, true),
      onPointerCancel: () => stop(true),
      onLostPointerCapture: () => stop(),
    },
  };
}
